import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { _ as _slicedToArray } from '../../unsorted/slicedToArray.js';
import { _ as _objectWithoutProperties } from '../../unsorted/objectWithoutProperties.js';
import { useId, useState, useEffect, useCallback, useMemo } from 'react';
import { DEFAULT_TOOLTIP_SIZE } from '../../constants.js';
import { u as useTooltipStore, U as UPDATE_TOOLTIP_CONTENT, C as CLEAR_TOOLTIP, S as STORE_TOOLTIP } from '../../providers/tooltipContext/tooltipContext.js';
import { jsx } from '@emotion/react/jsx-runtime';
import '@emotion/react';
import { B as Box } from '../Box/Box.js';
import { S as SmartClone } from '../SmartClone/SmartClone.js';
import { r as recursivelyAddTooltipAttrsToChildNodes, T as TooltipComponentContext } from '../shared/shared2.js';
import { useForwardLocalDomRef } from '../../hooks/useForwardLocalDomRef.js';
import { useGetSubcomponentChild } from '../../hooks/useGetSubcompnentChild.js';

var _excluded$2 = ["children"];
function ownKeys$2(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$2(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$2(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$2(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function TooltipContent(_ref) {
  var children = _ref.children,
    props = _objectWithoutProperties(_ref, _excluded$2);
  return jsx(Box, _objectSpread$2(_objectSpread$2({}, props), {}, {
    children: children
  }));
}
TooltipContent.displayName = "Tooltip.Content";

var _excluded$1 = ["children"];
function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function TooltipTarget(_ref) {
  var children = _ref.children,
    props = _objectWithoutProperties(_ref, _excluded$1);
  return jsx(SmartClone, _objectSpread$1(_objectSpread$1({}, props), {}, {
    children: children
  }));
}
TooltipTarget.displayName = "Tooltip.Target";

var _excluded = ["id", "children", "size", "domRef"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function Tooltip(_ref) {
  var idProp = _ref.id,
    children = _ref.children,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_TOOLTIP_SIZE : _ref$size,
    _ref$domRef = _ref.domRef,
    domRef = _ref$domRef === void 0 ? {
      current: null
    } : _ref$domRef,
    props = _objectWithoutProperties(_ref, _excluded);
  var localDomRef = useForwardLocalDomRef(domRef);
  var fallbackId = useId();
  var id = idProp !== null && idProp !== void 0 ? idProp : fallbackId;
  var target = useGetSubcomponentChild(children, TooltipTarget);
  var content = useGetSubcomponentChild(children, TooltipContent);
  var _useTooltipStore = useTooltipStore(function (state) {
      return state;
    }),
    dispatchAction = _useTooltipStore.dispatchAction;
  var _useState = useState(""),
    _useState2 = _slicedToArray(_useState, 2),
    currentTooltipId = _useState2[0],
    setCurrentTooltipId = _useState2[1];

  // @NOTE: this useEffect allows Modal content to stay up to date,
  // as the application re-renders and children potentially change
  useEffect(function () {
    dispatchAction({
      type: UPDATE_TOOLTIP_CONTENT,
      payload: {
        id: id,
        content: content
      }
    });
  }, [content, id, dispatchAction]);

  // @NOTE: we cannot have the uuid change, when this component re-renders,
  // so, we need to set it once and only once at component onMount.
  useEffect(function () {
    setCurrentTooltipId(id);
  }, [id]);

  // @NOTE: clean up the tooltip when the component is unmounted
  useEffect(function () {
    return function () {
      return dispatchAction({
        type: CLEAR_TOOLTIP,
        payload: {
          id: id
        }
      });
    };
  }, [id, dispatchAction]);

  // @NOTE: recursively add tooltip attributes to all child nodes
  // whenever things change
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(function () {
    if (currentTooltipId) {
      recursivelyAddTooltipAttrsToChildNodes(localDomRef.current, currentTooltipId, true);
    }
  }, [currentTooltipId, localDomRef, children]);
  useEffect(function () {
    var _localDomRef$current;
    var targetRect = (_localDomRef$current = localDomRef.current) === null || _localDomRef$current === void 0 ? void 0 : _localDomRef$current.getBoundingClientRect();
    dispatchAction({
      type: STORE_TOOLTIP,
      payload: {
        id: id,
        size: size,
        content: content,
        targetRect: targetRect
      }
    });
  }, [id, size, content, localDomRef, dispatchAction]);
  var triggerDomRecrawl = useCallback(function () {
    recursivelyAddTooltipAttrsToChildNodes(localDomRef.current, currentTooltipId, true);
  }, [currentTooltipId, localDomRef]);
  var contextValue = useMemo(function () {
    return {
      triggerDomRecrawl: triggerDomRecrawl
    };
  }, [triggerDomRecrawl]);
  return target ? jsx(TooltipComponentContext.Provider, {
    value: contextValue,
    children: jsx(SmartClone, _objectSpread(_objectSpread({}, props), {}, {
      id: id,
      domRef: localDomRef,
      children: target
    }))
  }) : null;
}
Tooltip.displayName = "Tooltip";
Tooltip.Target = TooltipTarget;
Tooltip.Content = TooltipContent;

export { Tooltip as T };
//# sourceMappingURL=Tooltip.js.map
